import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/MailOutlineRounded';
import { Box, IconButton, Stack, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from "notistack";
import React, { useContext } from "react";
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { ReactComponent as Email2Icon } from "../../../assets/icons/email-2.svg";
import { AuthContext } from "../../../contexts/AuthContext";
import { getGroupEmail } from "../../../helpers/authUser";

const useStyles = makeStyles((theme) => ({
    card: {
        position: 'relative',
        minWidth: 255,
        width: 255,
        height: 425,
        padding: '24px 16px',
        borderRadius: 16,
        backgroundColor: '#fff',
        border: `1px solid #e2e8f0`,
        transition: 'all 300ms ease-in-out',
        transformOrigin: 'center center',
        "@media (max-height: 750px)": { transform: 'scale(0.95)' },
        "@media (max-height: 700px)": { transform: 'scale(0.9)' },
        "@media (max-height: 650px)": { transform: 'scale(0.85)' },
        "@media (max-height: 600px)": { transform: 'scale(0.8)' },
        "@media (max-height: 550px)": { transform: 'scale(0.75)' },
        "@media (max-height: 500px)": { transform: 'scale(0.7)' },
        "@media (max-height: 450px)": { transform: 'scale(0.65)' },
        "@media (max-height: 400px)": { transform: 'scale(0.6)' },
        "&:hover": {
            borderColor: '#1eb6a8',
            boxShadow: '0 8px 24px rgba(30, 182, 168, 0.12)',
        },
    },
    mainIcon: {
        width: 20,
        height: 20,
        stroke: '#1eb6a8',
        fill: 'none',
        strokeWidth: '2',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
    },
    mainIconContainer: {
        width: 40,
        height: 40,
        background: '#f0fdf9',
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px solid #d1f5ee',
        flexShrink: 0,
    },
    subIcon: {
        width: 24,
        height: 24,
        fill: '#64748b',
    },
    leftBulletIcon: {
        width: 24,
        height: 24,
        fill: theme.colors.primary['300'],
        backgroundColor: '#fff',
        transform: 'translateX(-12px)',
    },
    longLine: {
        width: '100%',
        borderBottom: `3px solid ${theme.colors.primary['200']}`,
        marginLeft: '-16px',
        marginRight: '-12px',
        zIndex: 1,
    },
    rightArrowIcon : {
        width: 32,
        height: 32,
        fill: theme.colors.primary['300'],
        zIndex: 1,
    },
    title: {
        fontFamily: 'Lato',
        fontSize: '1.1rem',
        fontWeight: 600,
        color: '#1e293b',
    },
    linkWrapper: {
        marginTop: 'auto',
        padding: '12px 16px',
        background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
        borderRadius: 12,
        border: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
    },
    link: {
        display: '-webkit-box',
        textDecoration: 'none',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'normal',
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical',
        wordBreak: 'break-all',
        fontFamily: 'Lato',
        fontSize: '0.85rem',
        fontWeight: '600',
        color: '#1eb6a8',
        cursor: 'pointer',
    },
    copyButton: {
        width: '30px',
        height: '30px',
        background: '#e5e7eb',
        color: '#64748b',
        border: 'none',
        borderRadius: '8px',
        fontSize: '12px',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
	connector: {
		position: 'relative',
		top: '50%',
		right: 0,
        width: 30,
		height: 2,
		backgroundColor: '#cbd5e1',
		transform: 'translateY(-50%)',
		display: 'none',
		[theme.breakpoints.up('lg')]: {
		display: 'block'
		},
		'&::before': {
		content: '""',
		position: 'absolute',
		top: -4,
		right: -1,
		width: 0,
		height: 0,
		borderLeft: '6px solid #1eb6a8',
		borderTop: '5px solid transparent',
		borderBottom: '5px solid transparent'
		}
	},
}));

const NewEmail: React.FC<{}> = () => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const { userGroup } = useContext(AuthContext);

    return (<>
        <Stack direction="row" alignItems="center" justifyContent="flex-start" width="100%">
            <Stack className={classes.card} spacing={2} alignItems="flex-start">
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start">
                    <Box className={classes.mainIconContainer}>
                        <Email2Icon className={classes.mainIcon} />
                    </Box>
                    <Typography className={classes.title}>
                        {'New Email'}
                    </Typography>
                </Stack>
                <Typography fontFamily="Lato" fontSize="0.85rem" color={`#64748b`}>
                    {'Forward deal emails to your dedicated pipeline address'}
                </Typography>
                <Stack spacing={1} className={classes.linkWrapper} alignItems="flex-start" width="100%">
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" width="100%">
                        <EmailIcon className={classes.subIcon} />
                        <Typography className={classes.link}
                            onClick={() => window.open(`mailto: ${getGroupEmail(userGroup)}`, '_self', 'noopener, noreferrer')}>
                            {getGroupEmail(userGroup)}
                        </Typography>
                        <CopyToClipboard text={`${getGroupEmail(userGroup)}`}
                            options={{ format: "text/plain" }}
                            onCopy={() => {
                                enqueueSnackbar(`Successfully copied to clipboard.`,
                                    { anchorOrigin: { vertical: "bottom", horizontal: "right" }, });
                            }}>
                            <IconButton className={classes.copyButton} size="small">
                                <ContentCopyIcon sx={{ width: 18, height: 18, }} />
                            </IconButton>
                        </CopyToClipboard>
                    </Stack>
                </Stack>
            </Stack>
			<Box className={classes.connector} />
        </Stack>
    </>);
}

export default NewEmail;
